import {
  ApiKey,
  ApiKeyResponse,
  EntitlementsFeatureTooltip,
  JupiterTheme,
  useAsyncEffect,
} from '@jupiterone/jupiter-web-toolkit';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import { useArrayState, useBooleanState } from 'react-use-object-state';
import useSharedUsersAndAccountStyles from 'navbar/src/jupiterAccountSettings/AccountSettings/useSharedUsersAndAccountStyles';
import ApiKeysTable from './ApiKeysTable';
import CreateNewApiKey from './CreateNewApiKey';
import { FeatureEntitlementName } from '@jupiterone/entitlement-types';
import useAPIKey from 'navbar/src/jupiterAccountSettings/hooks/useApiKey';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    noResultsText: {
      color: theme.palette.coolGrey[50],
    },
    table: {
      marginTop: theme.spacing(6),
      height: 'unset',
    },
    errorText: {
      marginBottom: theme.spacing(3),
    },
    progressBar: {
      width: '100%',
      height: 2,
    },
  })
);

const ERROR_MESSAGE =
  'Error loading api keys. If you just enabled api key access and are seeing this error, please wait a few minutes and then check back.';
const NO_KEYS_FOUND_MESSAGE = 'No api keys found.';
const UPDATING_MESSAGE =
  'Updating access policies to enable API key access. Please check back in a few mins.';
export { ERROR_MESSAGE, NO_KEYS_FOUND_MESSAGE, UPDATING_MESSAGE };

const ApiKeysPage = () => {
  const classes = useStyles();
  const sharedClasses = useSharedUsersAndAccountStyles();
  const { deleteApiKey, getApiKeys } = useAPIKey();
  const apiKeys = useArrayState<ApiKey>([]);
  const loading = useBooleanState(true);
  const error = useBooleanState(false);

  useAsyncEffect(async () => {
    try {
      const data: ApiKeyResponse = await getApiKeys();
      apiKeys.setState(data.items);
    } catch (e) {
      error.setTrue();
    }
    loading.setFalse();
  }, []);

  const handleApiKeyCreated = (newApiKey: ApiKey) => {
    const sanitizedKey =
      '*********' + newApiKey.key.substring(newApiKey.key.length - 10);
    apiKeys.push({ ...newApiKey, key: sanitizedKey });
  };

  const handleDeleteClick = async (index: number) => {
    apiKeys.deleteAt(index);
    const apiKeyId = apiKeys.state[index].id;
    await deleteApiKey(apiKeyId);
  };

  const createKeyWindowOpen = useBooleanState(false);

  if (loading.state) {
    return (
      <div className={sharedClasses.centerXY}>
        <CircularProgress />
      </div>
    );
  }

  if (error.state) {
    <div className={sharedClasses.centerXY}>
      <Typography
        variant="h6"
        className={clsx(classes.noResultsText, sharedClasses.centerContent)}
      >
        {ERROR_MESSAGE}
      </Typography>
    </div>;
  }

  if (createKeyWindowOpen.state) {
    return (
      <CreateNewApiKey
        onBackButtonClick={createKeyWindowOpen.setFalse}
        onApiKeyCreated={handleApiKeyCreated}
      />
    );
  }

  return (
    <div className={sharedClasses.fadeIn}>
      <div className={sharedClasses.headerLayout}>
        <Typography
          variant="h6"
          className={sharedClasses.groupPanelSubheaderText}
        >
          Your API Keys
        </Typography>
        <EntitlementsFeatureTooltip
          entitlement={FeatureEntitlementName.API_ACCESS}
        >
          {(hasEntitlement) => (
            <Button
              variant="outlined"
              size="small"
              onClick={createKeyWindowOpen.setTrue}
              disabled={!hasEntitlement}
              classes={{
                root: sharedClasses.button,
                disabled: sharedClasses.buttonDisabled,
              }}
            >
              New API Key
            </Button>
          )}
        </EntitlementsFeatureTooltip>
      </div>
      {apiKeys.state.length > 0 ? (
        <ApiKeysTable
          apiKeys={apiKeys.state}
          onDeleteClick={handleDeleteClick}
          className={clsx(sharedClasses.centerContent, classes.table)}
        />
      ) : (
        <Typography
          variant="h6"
          className={clsx(classes.noResultsText, sharedClasses.centerContent)}
        >
          {NO_KEYS_FOUND_MESSAGE}
        </Typography>
      )}
    </div>
  );
};

export default ApiKeysPage;
